import {
  Dispatch,
  forwardRef,
  ReactNode,
  RefObject,
  SetStateAction,
  UIEventHandler,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { Box, Divider, List, MenuItem, Typography } from '@mui/material'

type SuggestedSearchesProps = {
  suggestedSearches: [string, string][]
  setInputValue: Dispatch<SetStateAction<string>>
}

function SuggestedSearches({
  suggestedSearches,
  setInputValue,
}: SuggestedSearchesProps) {
  return (
    <Box sx={{ pt: 1 }}>
      {suggestedSearches.map(([key, suggestion]) => (
        <MenuItem
          key={key}
          onClick={() => {
            setInputValue(suggestion || '')
          }}
          sx={{ py: 0.5 }}
        >
          <Typography variant="caption" color="text.secondary">
            Suggested Search: {suggestion}
          </Typography>
        </MenuItem>
      ))}

      {suggestedSearches.length > 0 && <Divider flexItem sx={{ mb: -1 }} />}
    </Box>
  )
}

type InfiniteScrollListBoxProps = {
  children: ReactNode
  hasInputValue?: boolean
  popperScrollRef?: RefObject<number>
  listboxRef?: RefObject<HTMLUListElement | null>
  setInputValue?: Dispatch<SetStateAction<string>>
  suggestedSearches?: [string, string][]
  hasNextPage?: boolean
  fetchNextPage?: () => void
  isFetching?: boolean
  optionsLength?: number
}

/**
 * Shared ListBox component for autocomplete dropdowns with infinite scroll support.
 * - Preserves scroll position across re-renders
 * - Auto-fetches next page if content fits without scrollbar
 * - Supports suggested searches (optional)
 */
export const InfiniteScrollListBox = forwardRef(
  (
    {
      children,
      hasInputValue = false,
      popperScrollRef,
      listboxRef,
      suggestedSearches,
      setInputValue,
      hasNextPage,
      fetchNextPage,
      isFetching,
      optionsLength,
      ...props
    }: InfiniteScrollListBoxProps,
    ref: React.Ref<HTMLUListElement>,
  ) => {
    const listRef = useRef<HTMLUListElement>(null)
    const hasCheckedScrollableRef = useRef(false)

    // Preserve scroll position across re-renders (MUI autocomplete resets it)
    useEffect(() => {
      if (popperScrollRef?.current) {
        setTimeout(() => {
          listRef.current?.scrollTo({
            left: 0,
            top: popperScrollRef.current,
            behavior: 'instant',
          })
        }, 0)
      }
    }, [popperScrollRef])

    // Reset auto-fetch check when options change
    useEffect(() => {
      hasCheckedScrollableRef.current = false
    }, [optionsLength])

    // Auto-fetch next page if content doesn't overflow (no scrollbar) - only check once
    useEffect(() => {
      const checkIfScrollable = () => {
        if (
          listRef.current &&
          hasNextPage &&
          fetchNextPage &&
          !isFetching &&
          !hasCheckedScrollableRef.current
        ) {
          const { scrollHeight, clientHeight } = listRef.current
          // If content fits without scrolling, fetch more
          if (scrollHeight <= clientHeight) {
            hasCheckedScrollableRef.current = true
            fetchNextPage()
          }
        }
      }

      // Check after render completes
      const timer = setTimeout(checkIfScrollable, 100)
      return () => clearTimeout(timer)
    }, [hasNextPage, fetchNextPage, isFetching, optionsLength])

    return (
      <List disablePadding {...props} ref={ref}>
        {!hasInputValue && suggestedSearches && setInputValue && (
          <SuggestedSearches
            suggestedSearches={suggestedSearches}
            setInputValue={setInputValue}
          />
        )}
        <ul
          {...props}
          ref={(node) => {
            listRef.current = node
            if (listboxRef) {
              listboxRef.current = node
            }
          }}
        >
          {children}
        </ul>
      </List>
    )
  },
)

/**
 * Scroll event handler for infinite scroll in autocomplete listbox.
 * Call fetchNextPage when user scrolls to bottom.
 */
export function createOnScrollHandler(
  hasNextPage?: boolean,
  fetchNextPage?: () => void,
  popperScrollRef?: RefObject<number>,
  isFetching?: boolean,
): UIEventHandler<HTMLUListElement> {
  return (event) => {
    const target = event.target as HTMLDivElement
    const isAtBottom =
      Math.abs(target.clientHeight - (target.scrollHeight - target.scrollTop)) <
      5

    if (isAtBottom && hasNextPage && fetchNextPage && !isFetching) {
      fetchNextPage()
    }

    if (popperScrollRef) {
      popperScrollRef.current = target.scrollTop
    }
  }
}

/**
 * Wires an MUI Autocomplete listbox for infinite scroll: owns the scroll refs,
 * builds the scroll handler, and assembles the `slotProps.listbox` object that
 * mounts `InfiniteScrollListBox`. Shared by the autocomplete pickers (e.g.
 * `LiveListPicker`, `ExcludedVendorsField`) so the wiring lives in one place.
 */
export function useInfiniteScrollListbox({
  hasNextPage,
  fetchNextPage,
  isFetching,
  optionsLength,
}: {
  hasNextPage?: boolean
  fetchNextPage?: () => void
  isFetching?: boolean
  optionsLength: number
}) {
  const listboxRef = useRef<HTMLUListElement | null>(null)
  const popperScrollRef = useRef(0)

  const onScroll = useMemo(
    () =>
      createOnScrollHandler(
        hasNextPage,
        fetchNextPage,
        popperScrollRef,
        isFetching,
      ),
    [hasNextPage, fetchNextPage, isFetching],
  )

  const listboxSlotProps = {
    onScroll,
    sx: {
      mt: -1,
      pb: 0,
      // 40vh matches the default max height of the autocomplete, so the list
      // resizes when the window resizes.
      maxHeight: 'min(40vh, 500px)',
    },
    component: InfiniteScrollListBox,
    popperScrollRef,
    listboxRef,
    hasNextPage,
    fetchNextPage,
    isFetching,
    optionsLength,
  }

  return { listboxRef, popperScrollRef, listboxSlotProps }
}
